'use client'

import { useRouter } from 'next/navigation'
import { ArrowLeft, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CasePrescription,
  toMockCaseRecord,
  toMockSteps,
  useCasePrescriptionSteps,
  usePreviewCaseForFlow,
  type FlowType,
} from '@/features/cases'
import { useInvestorDetail } from '@/features/investors'

/**
 * Staff-only, read-only, full-page prescription-steps view — reached from `ClientDashboardPreview`'s
 * "N steps to get there" CTA. A separate page (not a dialog, per direct feedback) so it reads the
 * same way `client-detail.tsx`'s own prescription section does, just full-width and standalone.
 * Same `canEdit={false}` rendering admin sees — no add/edit/remove/reorder/toggle affordances.
 */
export function ClientPrescriptionPreview({
  investorId,
  flow,
}: {
  investorId: string
  flow: FlowType
}) {
  const router = useRouter()
  const { data: investor } = useInvestorDetail(investorId)
  const query = usePreviewCaseForFlow(investorId, flow)
  const stepsQuery = useCasePrescriptionSteps(query.data?.id)

  return (
    <div className="flex flex-col gap-4">
      <Button variant="ghost" size="sm" className="-ml-2 w-fit" onClick={() => router.back()}>
        <ArrowLeft className="size-4" /> Back
      </Button>

      <div className="border-primary/20 bg-primary/5 flex items-center gap-2 rounded-lg border px-3 py-2">
        <Eye className="text-primary size-4 shrink-0" />
        <p className="text-xs leading-snug">
          <span className="font-semibold">Investor View</span> — this is exactly what{' '}
          {investor?.name ?? 'this investor'} will see once this service is published. Not editable
          from here.
        </p>
      </div>

      {query.isListLoading || query.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !query.data ? (
        <Empty className="border border-dashed py-12">
          <EmptyHeader>
            <EmptyTitle>Nothing to preview</EmptyTitle>
            <EmptyDescription>
              This investor doesn&apos;t have a service in this state to show prescription steps
              for.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Card>
          <CardContent>
            <CasePrescription
              caseRecord={toMockCaseRecord(query.data)}
              canEdit={false}
              injectedSteps={stepsQuery.data ? toMockSteps(stepsQuery.data) : []}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}
